import { ref, computed } from 'vue'
import { UserManager, User as OidcUser, WebStorageStateStore } from 'oidc-client-ts'
import * as Sentry from '@sentry/vue'
import { defaultOidcConfig } from '@/config/oidc'
import { sentryConfig } from '@/config/sentry'
import type { User } from '@/types/auth'
import {
  useCrossTabAuth,
  fastCheckAuth,
  acquireRefreshLock,
  releaseRefreshLock,
  waitForRefresh,
} from './useCrossTabAuth'

// Create a singleton UserManager instance
export const userManager = new UserManager({
  authority: defaultOidcConfig.authority,
  client_id: defaultOidcConfig.client_id,
  redirect_uri: defaultOidcConfig.redirect_uri,
  response_type: defaultOidcConfig.response_type,
  scope: defaultOidcConfig.scope,
  post_logout_redirect_uri: defaultOidcConfig.post_logout_redirect_uri,
  silent_redirect_uri: defaultOidcConfig.silent_redirect_uri,
  automaticSilentRenew: defaultOidcConfig.automaticSilentRenew,
  loadUserInfo: defaultOidcConfig.loadUserInfo,
  monitorSession: defaultOidcConfig.monitorSession,
  stateStore: new WebStorageStateStore({ store: window.localStorage }),
  userStore: new WebStorageStateStore({ store: window.localStorage }),
})

// Initialize cross-tab auth manager
const crossTabAuth = useCrossTabAuth()

// Will be set up after login function is defined
let handleSilentRenewError: (() => void) | null = null

// Shared reactive state - singleton-like behavior
const user = ref<User | null>(null)
const error = ref<string | null>(null)

const isAuthenticated = computed(() => !!user.value)
const token = computed(() => {
  // Get the current user from UserManager and return the access token
  return userManager.getUser().then((oidcUser) => oidcUser?.access_token || null)
})

const mapOidcUserToAppUser = (oidcUser: OidcUser): User => {
  return {
    id: oidcUser.profile.sub || '',
    username: oidcUser.profile.name || oidcUser.profile.email || '',
    email: oidcUser.profile.email || '',
    firstName: oidcUser.profile.name || '',
    lastName: oidcUser.profile.family_name || '',
    isActive: true,
    lastLogin: new Date(),
  }
}

const login = async () => {
  error.value = null

  try {
    await userManager.signinRedirect()
  } catch (err) {
    error.value = err instanceof Error ? err.message : 'Login failed'
    console.error('Login error:', err)
    if (sentryConfig.enabled) {
      Sentry.captureException(err, {
        tags: {
          errorType: 'auth',
          authAction: 'login',
        },
      })
    }
  }
}

// Add event handler for silent renewal failure (after login is defined)
handleSilentRenewError = () => {
  console.log('Silent renewal failed, redirecting to login')
  login()
}
userManager.events.addSilentRenewError(handleSilentRenewError)

/** Clears OIDC user from storage without IdP redirect. Use when the server invalidated the session (e.g. after password change). */
const clearLocalAuthSession = async () => {
  try {
    await userManager.removeUser()
  } catch (err) {
    console.error('clearLocalAuthSession (removeUser) error:', err)
  }
  user.value = null
  if (sentryConfig.enabled) {
    Sentry.setUser(null)
  }
  crossTabAuth.broadcastUserLoggedOut()
}

const logout = async () => {
  try {
    await userManager.signoutRedirect()
    user.value = null
    // Clear user context in Sentry
    if (sentryConfig.enabled) {
      Sentry.setUser(null)
    }
    // Broadcast logout to other tabs
    crossTabAuth.broadcastUserLoggedOut()
  } catch (err) {
    console.error('Logout error:', err)
    user.value = null
    if (sentryConfig.enabled) {
      Sentry.setUser(null)
      Sentry.captureException(err, {
        tags: {
          errorType: 'auth',
          authAction: 'logout',
        },
      })
    }
    crossTabAuth.broadcastUserLoggedOut()
  }
}

const checkAuth = async () => {
  try {
    const oidcUser = await userManager.getUser()
    if (oidcUser && !oidcUser.expired) {
      user.value = mapOidcUserToAppUser(oidcUser)
      // Set user context in Sentry
      if (sentryConfig.enabled) {
        Sentry.setUser({
          id: user.value.id,
          email: user.value.email,
          username: user.value.username,
        })
      }
    } else {
      user.value = null
      if (sentryConfig.enabled) {
        Sentry.setUser(null)
      }
    }
  } catch (err) {
    console.error('Auth check error:', err)
    user.value = null
    if (sentryConfig.enabled) {
      Sentry.setUser(null)
      Sentry.captureException(err, {
        tags: {
          errorType: 'auth',
          authAction: 'checkAuth',
        },
      })
    }
  }
}

const handleCallback = async (): Promise<User | null> => {
  try {
    const oidcUser = await userManager.signinRedirectCallback()
    user.value = mapOidcUserToAppUser(oidcUser)
    // Set user context in Sentry
    if (sentryConfig.enabled) {
      Sentry.setUser({
        id: user.value.id,
        email: user.value.email,
        username: user.value.username,
      })
    }
    // Broadcast login to other tabs
    crossTabAuth.broadcastTokenUpdate()
    return user.value
  } catch (err) {
    console.error('Callback error:', err)
    error.value = 'Authentication callback failed'
    if (sentryConfig.enabled) {
      Sentry.captureException(err, {
        tags: {
          errorType: 'auth',
          authAction: 'callback',
        },
      })
    }
    return null
  }
}

const handleAuthFailure = async () => {
  console.log('Authentication failed, redirecting to OIDC...')
  user.value = null
  error.value = null
  // Clear user context in Sentry
  if (sentryConfig.enabled) {
    Sentry.setUser(null)
  }

  try {
    await login()
  } catch (err) {
    console.error('Failed to redirect to OIDC:', err)
    if (sentryConfig.enabled) {
      Sentry.captureException(err, {
        tags: {
          errorType: 'auth',
          authAction: 'handleAuthFailure',
        },
      })
    }
  }
}

const getAccessToken = async (): Promise<string | null> => {
  try {
    const oidcUser = await userManager.getUser()
    return oidcUser?.access_token || null
  } catch (err) {
    console.error('Error getting access token:', err)
    return null
  }
}

const renewToken = async (): Promise<boolean> => {
  try {
    // Try to acquire refresh lock
    const lockAcquired = await acquireRefreshLock()

    if (!lockAcquired) {
      // Another tab is refreshing, wait for it to complete
      console.log('Another tab is refreshing token, waiting...')
      const refreshCompleted = await waitForRefresh()

      if (refreshCompleted) {
        // Refresh completed by another tab, reload user from localStorage
        const fastResult = fastCheckAuth()
        if (fastResult.isValid && fastResult.user) {
          user.value = mapOidcUserToAppUser(fastResult.user)
          // Set user context in Sentry
          if (sentryConfig.enabled) {
            Sentry.setUser({
              id: user.value.id,
              email: user.value.email,
              username: user.value.username,
            })
          }
          return true
        }
        // If still invalid, try to refresh ourselves
        return await renewToken()
      }

      // Wait failed, try to refresh anyway
      console.warn('Wait for refresh timed out, attempting refresh anyway')
    }

    // We have the lock, proceed with refresh
    try {
      const oidcUser = await userManager.signinSilent()
      if (oidcUser) {
        user.value = mapOidcUserToAppUser(oidcUser)
        // Set user context in Sentry
        if (sentryConfig.enabled) {
          Sentry.setUser({
            id: user.value.id,
            email: user.value.email,
            username: user.value.username,
          })
        }
        // Broadcast update to other tabs
        crossTabAuth.broadcastTokenUpdate()
        return true
      }
      return false
    } finally {
      // Always release the lock
      await releaseRefreshLock()
    }
  } catch (err) {
    console.error('Token renewal error:', err)
    // Release lock on error
    await releaseRefreshLock()
    if (sentryConfig.enabled) {
      Sentry.captureException(err, {
        tags: {
          errorType: 'auth',
          authAction: 'renewToken',
        },
      })
    }
    return false
  }
}

const clearError = () => {
  error.value = null
}

// Set up UserManager event handlers for cross-tab coordination
const setupUserManagerEvents = () => {
  // User loaded - broadcast to other tabs
  userManager.events.addUserLoaded((loadedUser) => {
    if (loadedUser) {
      user.value = mapOidcUserToAppUser(loadedUser)
      // Set user context in Sentry
      if (sentryConfig.enabled) {
        Sentry.setUser({
          id: user.value.id,
          email: user.value.email,
          username: user.value.username,
        })
      }
      crossTabAuth.broadcastTokenUpdate()
    }
  })

  // User unloaded - clear state
  userManager.events.addUserUnloaded(() => {
    user.value = null
    // Clear user context in Sentry
    if (sentryConfig.enabled) {
      Sentry.setUser(null)
    }
    crossTabAuth.broadcastUserLoggedOut()
  })

  // Token expiring - coordinate refresh
  userManager.events.addAccessTokenExpiring(() => {
    crossTabAuth.broadcastTokenExpiring()
  })

  // Token expired - notify all tabs
  userManager.events.addAccessTokenExpired(() => {
    user.value = null
    // Clear user context in Sentry
    if (sentryConfig.enabled) {
      Sentry.setUser(null)
    }
    crossTabAuth.broadcastTokenExpired()
  })
}

if (typeof window !== 'undefined') {
  setupUserManagerEvents()
}

export function useAuth() {
  return {
    // State
    user,
    error,
    token,

    // Computed
    isAuthenticated,

    // Actions
    login,
    logout,
    clearLocalAuthSession,
    checkAuth,
    handleCallback,
    handleAuthFailure,
    getAccessToken,
    renewToken,

    // Clear error
    clearError,
  }
}
